import { useState } from 'react';
import { api, type Driver } from '../api';
import { Banner, Card, Empty, Loading, useAsync, when } from '../ui';

const SORTS: { value: string; label: string; hint: string }[] = [
  { value: 'earned', label: 'Most earned', hint: 'By commission actually paid out' },
  { value: 'referrals', label: 'Most referrals', hint: 'By drivers recruited' },
  { value: 'flagged', label: 'Most flagged', hint: 'Recruiters whose referrals need review' },
  { value: 'recent', label: 'Most recent', hint: 'By latest activity' },
];

const KYC_TONE: Record<string, string> = {
  verified: 'ok', failed: 'danger', in_progress: 'info', pending: 'warn', unverified: '',
};

/**
 * Drivers, ranked.
 *
 * The engine is referral-centric; the people running the programme are not.
 * They want to know who is bringing drivers in, what that has cost, and which
 * recruiters keep producing referrals a human has to look at.
 */
export function Drivers({ open }: { open: (driverRef: string) => void }) {
  const [sort, setSort] = useState('earned');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');

  const { data, error, loading } = useAsync(
    () => api.drivers({ sort, search: applied || undefined, limit: '100' }),
    [sort, applied],
  );

  const drivers = data?.drivers ?? [];
  const totals = drivers.reduce(
    (a, d) => ({
      made: a.made + d.referrals_made,
      paid: a.paid + d.referrals_paid,
      earned: a.earned + BigInt(d.total_earned),
    }),
    { made: 0, paid: 0, earned: 0n },
  );

  return (
    <div className="stack">
      <Card flush>
        <div className="card-head">
          <select value={sort} onChange={(e) => setSort(e.target.value)} style={{ width: 170 }}>
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <span className="small muted">{SORTS.find((s) => s.value === sort)?.hint}</span>
          <form className="row grow" onSubmit={(e) => { e.preventDefault(); setApplied(search.trim()); }}>
            <input
              type="search"
              placeholder="Driver id, verified name, or phone number"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className="btn" type="submit">Search</button>
            {applied && <button className="btn ghost" type="button" onClick={() => { setSearch(''); setApplied(''); }}>Clear</button>}
          </form>
        </div>

        {error && <div style={{ padding: 18 }}><Banner tone="danger" title="Could not load drivers">{error}</Banner></div>}
        {loading && <Loading />}

        {!loading && data && (drivers.length === 0 ? (
          <Empty title="No recruiters yet" hint="Drivers appear here once they have referred someone." />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 34 }} className="num">#</th>
                  <th>Driver</th>
                  <th>Identity</th>
                  <th>KYC</th>
                  <th className="num">Referred</th>
                  <th className="num">Paid</th>
                  <th className="num">Earned</th>
                  <th className="num">Pending</th>
                  <th>Attention</th>
                  <th className="num">Last active</th>
                </tr>
              </thead>
              <tbody>
                {drivers.map((d, i) => (
                  <Row key={d.driver_ref} d={d} rank={i + 1} showRank={sort !== 'recent'} onOpen={() => open(d.driver_ref)} />
                ))}
              </tbody>
            </table>
          </div>
        ))}

        {!loading && drivers.length > 0 && (
          <div className="card-head" style={{ borderBottom: 'none', borderTop: '1px solid var(--rule)' }}>
            <span className="small muted">
              {drivers.length} recruiters · {totals.made} referred · {totals.paid} paid
            </span>
            <div className="spacer" />
            <span className="small">
              <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
                ₦{(totals.earned / 100n).toLocaleString()}
              </strong>{' '}
              <span className="muted">commission paid</span>
            </span>
          </div>
        )}
      </Card>
    </div>
  );
}

function Row({ d, rank, showRank, onOpen }: { d: Driver; rank: number; showRank: boolean; onOpen: () => void }) {
  const attention: string[] = [];
  if (d.referrals_flagged > 0) attention.push(`${d.referrals_flagged} flagged`);
  if (d.shared_account_with > 0) attention.push(`account shared with ${d.shared_account_with}`);
  if (d.referrals_rejected > 0) attention.push(`${d.referrals_rejected} rejected`);

  return (
    <tr className="clickable" onClick={onOpen} tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}>
      <td className="num muted small">{showRank ? rank : ''}</td>
      <td>
        <div style={{ fontWeight: 550 }}>{d.verified_name ?? <span className="muted">Not yet verified</span>}</div>
        <div className="small muted mono">{d.driver_id}</div>
      </td>
      <td className="small mono muted">
        <div>{d.bvn_masked ?? '—'}</div>
        <div>{d.phone_masked ?? '—'}</div>
      </td>
      <td><span className={`pill ${KYC_TONE[d.kyc_status] ?? ''}`}>{d.kyc_status.replace(/_/g, ' ')}</span></td>
      <td className="num">{d.referrals_made}</td>
      <td className="num">{d.referrals_paid}</td>
      <td className="num" style={{ fontWeight: 550 }}>{d.total_earned_display}</td>
      <td className="num muted">{BigInt(d.pending_value) > 0n ? d.pending_value_display : '—'}</td>
      <td className="small">
        {attention.length === 0
          ? <span className="muted">—</span>
          : attention.map((a) => <div key={a} style={{ color: 'var(--warn)' }}>{a}</div>)}
      </td>
      <td className="num small muted">{when(d.last_activity, { time: false })}</td>
    </tr>
  );
}
